import { Injectable, computed, inject, signal } from '@angular/core';

import { UserStateService } from './user-state.service';
import { PermissionsService } from './permissions.service';
import { PathService } from './path.service';
import { GlobalsService } from './globals.service';

/**
 * The signed-in person's avatar, in ONE place.
 *
 * Every surface that draws "me" (topbar, bug-report thread bubbles, …) reads
 * from here, so the same person never appears with two different pictures or
 * two different sets of initials.
 *
 * Failure handling: the avatar is an absolute S3 URL built from the stored
 * serverName. When that object is missing the <img> would sit there as a
 * broken icon forever, so consumers bind `(error)="markFailed()"` and fall
 * back to `initials()`. Failures are remembered BY URL, so uploading a new
 * avatar (which changes the cache-busted URL) is retried rather than staying
 * suppressed by an earlier miss.
 */
@Injectable({ providedIn: 'root' })
export class UserAvatarService {
  private readonly userState = inject(UserStateService);
  private readonly perms = inject(PermissionsService);
  private readonly pathService = inject(PathService);
  private readonly globals = inject(GlobalsService);

  /**
   * Display name of the human at the keyboard: in a sub-user session that's
   * the team member, not the parent account the session technically runs as.
   */
  readonly displayName = computed(() => {
    const acting = this.perms.actingUser();
    if (acting) {
      // filter(Boolean) — names may be null/absent for email-only members;
      // interpolating would render the literal "null null".
      const name = [acting.firstName, acting.lastName].filter(Boolean).join(' ');
      return name || acting.email;
    }
    return this.userState.fullName();
  });

  readonly initials = computed(() => {
    const name = this.displayName();
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  });

  readonly avatarUrl = computed(() => {
    // Sub-user session: user() is the PARENT — don't show the parent's
    // avatar for the team member; fall back to the sub-user's initials.
    if (this.perms.actingUser()) return '';
    const user = this.userState.user();
    if (!user?.avatar) return '';

    const avatar = user.avatar as any;
    // Cache busting: _updatedAt is stamped when the avatar changes.
    const cacheBuster = avatar._updatedAt || avatar.id || '';
    const suffix = cacheBuster ? `?v=${cacheBuster}` : '';

    if (avatar.serverName) {
      const url = this.pathService.getAvatarUrl(avatar);
      return url ? url + suffix : '';
    }
    if (typeof avatar === 'string') {
      return avatar.startsWith('http')
        ? avatar + suffix
        : `${this.globals.storageUrl}${avatar}${suffix}`;
    }
    const imgUrl = this.globals.getImageUrl(avatar);
    return imgUrl ? imgUrl + suffix : '';
  });

  private readonly failedUrls = signal<ReadonlySet<string>>(new Set());

  /** True when there's an avatar image worth rendering (and it hasn't failed). */
  readonly showImage = computed(() => {
    const url = this.avatarUrl();
    return !!url && !this.failedUrls().has(url);
  });

  /** Bind to the <img> `(error)` — the current URL falls back to initials. */
  markFailed(): void {
    const url = this.avatarUrl();
    if (!url) return;
    this.failedUrls.update(set => new Set(set).add(url));
  }
}
